import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api } from '@/services/api';
import { Card, CardHeader } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { DataTable } from '@/components/tables/data-table';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';

export function ResourcePage({ title, path }) {
  const query = useQuery({
    queryKey: [path],
    queryFn: () => api(path).then((result) => result.data),
  });
  const rows = query.data ?? [];
  const keys = rows.length
    ? Object.keys(rows[0])
      .filter((key) => !['__v', 'passwordHash', 'tokenHash'].includes(key))
      .slice(0, 8)
    : [];
  const columns = keys.map((key) => ({
    key,
    header: key.replace(/([A-Z])/g, ' $1'),
    value: (row) => row[key],
    cell: (row) => format(row[key]),
  }));
  const tableId = `resource-${path.replaceAll(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

  return <div className="workspace-page">
    <PageHeader
      eyebrow="Observability"
      title={title}
      meta={<Badge tone="accent">{rows.length} records</Badge>}
      actions={<Button variant="secondary" onClick={() => query.refetch()}><RefreshCw size={14}/>Refresh</Button>}
    />
    <Card className="workspace-fill">
      <CardHeader eyebrow="Event stream" title={title}/>
      {rows.length ? <DataTable
        tableId={tableId}
        rows={rows}
        columns={columns}
        getKey={(row) => String(row._id ?? row.id ?? JSON.stringify(row))}
        onRefresh={() => query.refetch()}
        loading={query.isFetching}
        searchPlaceholder={`Search ${title.toLowerCase()}`}
      /> : <EmptyState title={query.isLoading ? `Loading ${title.toLowerCase()}` : `No ${title.toLowerCase()}`}/>} 
    </Card>
  </div>;
}

function format(value) {
  if (typeof value === 'boolean') return <Badge tone={value ? 'success' : 'neutral'}>{String(value)}</Badge>;
  if (typeof value === 'string' && ['active', 'completed', 'healthy', 'pending', 'closed', 'blocked', 'critical', 'warning'].includes(value)) {
    const tone = ['active', 'healthy', 'completed'].includes(value)
      ? 'success'
      : ['blocked', 'critical'].includes(value)
        ? 'error'
        : 'warning';
    return <Badge tone={tone}>{value}</Badge>;
  }
  if (value === null || value === undefined) return <span className="text-slate-700">—</span>;
  if (typeof value === 'object') return <code className="block max-w-60 truncate text-xs text-slate-500">{JSON.stringify(value)}</code>;
  return <span className="text-xs text-slate-400">{String(value)}</span>;
}
